import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import {
  AllAmenitiesViewModel,
  GalleryViewModel,
  HotelMapModel,
  OverviewViewModel,
  PrimaryPageMessagingModel,
  PropertyAdditionalInfoViewModel,
  PropertyInfoPriceGuaranteeViewModel,
  ReviewsViewModel,
  ScrollTarget,
  ShortlistModel,
  StickyOfferModel
} from 'models';
import { VrPropertySummaryViewModel } from 'models/hotel/hotel-details-types';

import { ToolbarType, UitkOverlayToolbar } from 'uitk-react-toolbar';
import { UitkCard, UitkCardContent } from 'uitk-react-cards';

// Components
import { HotelInfo } from 'components/infosite/property-details/hotel-info';
import { InfositeGallery } from 'components/infosite/property-details/infosite-gallery';

export type LocalizedPropertyDetailsProps = LocalizedComponentProps & PropertyDetailsProps;

export interface PropertyDetailsProps {
  hotelId: number;
  id: number;
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  additionalInfo?: PropertyAdditionalInfoViewModel;
  amenities?: AllAmenitiesViewModel;
  defaultData?: any;
  gallery?: GalleryViewModel;
  hotelName?: string;
  hotelOverviewA11yHeader?: string;
  englishHotelName?: string;
  overview?: OverviewViewModel;
  reviews?: ReviewsViewModel;
  stars?: string;
  starRatingIcon?: string;
  shortlistModel?: ShortlistModel;
  primaryPageMessagingModel?: PrimaryPageMessagingModel;
  mapModel?: HotelMapModel;
  singleUnitVrPropertySummary?: VrPropertySummaryViewModel;
  vipMessage?: string;
  stickyOffer?: StickyOfferModel;
  priceGuarantee?: PropertyInfoPriceGuaranteeViewModel;
  roomsAndRatesTarget?: ScrollTarget;
  goBack: () => void;
  favoriteButton?: JSX.Element;
  propertyTypeBadge?: string; // Experiment: 32599 Property_Type_Badging
}

@withLocalization
@inject('routeStore', 'analytics', 'currentHotel')
@observer
export class PropertyDetails extends React.Component<LocalizedPropertyDetailsProps> {
  public render() {
    const {
      defaultData,
      additionalInfo,
      amenities,
      analytics,
      currentHotel,
      l10n,
      hotelId,
      hotelName,
      hotelOverviewA11yHeader,
      englishHotelName,
      gallery,
      id,
      mapModel,
      overview,
      primaryPageMessagingModel,
      reviews,
      routeStore,
      starRatingIcon,
      stars,
      singleUnitVrPropertySummary,
      vipMessage,
      stickyOffer,
      priceGuarantee,
      roomsAndRatesTarget,
      favoriteButton,
      propertyTypeBadge,
      goBack
    } = this.props;
    const galleryComponent = (
      <InfositeGallery
        analytics={analytics}
        currentHotel={currentHotel}
        gallery={gallery}
        hotelId={hotelId}
        hotelName={hotelName}
        id={id}
        routeStore={routeStore}
        favoriteButton={favoriteButton}
      />
    );

    const hotelInfo = (withName = true) => (
      <HotelInfo
        additionalInfo={additionalInfo}
        amenities={amenities}
        hotelName={hotelName}
        englishHotelName={englishHotelName}
        mapModel={mapModel}
        overview={overview}
        primaryPageMessagingModel={primaryPageMessagingModel}
        reviews={reviews}
        starRatingIcon={starRatingIcon}
        stars={stars}
        singleUnitVrPropertySummary={singleUnitVrPropertySummary}
        vipMessage={vipMessage}
        stickyOffer={stickyOffer}
        priceGuarantee={priceGuarantee}
        roomsAndRatesTarget={roomsAndRatesTarget}
        withName={withName}
        currentHotel={currentHotel}
        propertyTypeBadge={propertyTypeBadge}
      />
    );

    /**
     * This is a bit of a hack to set a loaded state once we have data back for these items - the class name indicates
     *  we can remove the min-height on the container that avoids display jank/bounciness.
     */
    const status: string = defaultData ? 'initialized' : 'loaded';
    const cardClassName = `property-details all-b-margin-three ${status}`;
    const largeCardClassName = `${cardClassName} imagelayout-left-fullbleed`;

    const toolbarProps = {
      action: goBack,
      iconLabel: l10n.formatText('hotels.infosite.backToSearchResults.linkText'),
      type: ToolbarType.BACK
    };
    const actionLeftContentLabel = l10n.formatText('hotels.infosite.back.arrow.title');

    const smallView = (
      <>
        <UitkOverlayToolbar {...toolbarProps} actionContent={favoriteButton} overlap={true} />
        <UitkCard className={`${cardClassName} uitk-flat-border-top`}>
          {galleryComponent}
          <UitkCardContent>{hotelInfo()}</UitkCardContent>
        </UitkCard>
      </>
    );

    const mediumView = (
      <>
        <UitkOverlayToolbar {...toolbarProps} actionContent={favoriteButton} actionLeftContent={actionLeftContentLabel} overlap={true} />
        <UitkCard className={`${cardClassName} uitk-flat-border-top`}>
          {galleryComponent}
          <UitkCardContent>{hotelInfo()}</UitkCardContent>
        </UitkCard>
      </>
    );

    const largeView = (
      <UitkCard className={`${largeCardClassName} uitk-flat-border-top all-t-margin-two`}>
        <section className="uitk-cell uitk-card-media uitk-flat-border-top all-cell-2-3">{galleryComponent}</section>
        <div className="uitk-cell all-cell-1-3">{hotelInfo(false)}</div>
      </UitkCard>
    );

    return (
      <div itemScope={true} itemType="https://schema.org/Hotel">
        <h2 className="is-visually-hidden">{hotelOverviewA11yHeader}</h2>
        <Viewport>
          <ViewSmall>
            <React.Fragment>{smallView}</React.Fragment>
          </ViewSmall>
          <ViewMedium>
            <React.Fragment>{mediumView}</React.Fragment>
          </ViewMedium>
          <ViewLarge>
            <React.Fragment>{largeView}</React.Fragment>
          </ViewLarge>
        </Viewport>
      </div>
    );
  }
}
